import { useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, CATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { UnsubscribeModal } from "./UnsubscribeModal";

// The closing band of the paper: a white colophon ruled off in emerald,
// its four columns separated by vertical hairlines, closed by an emerald
// strip that answers the utility strip at the top of the masthead.
export function Footer() {
    const brand = useBrand();
    const [unsubOpen, setUnsubOpen] = useState(false);

    return (
        <footer className="mt-20 border-t-[3px] border-accent bg-surface" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 lg:divide-x lg:divide-line">
                    {/* Colophon */}
                    <div className="col-span-2 lg:col-span-1 lg:pr-8">
                        <span className="font-display text-2xl font-bold leading-none text-accent">
                            {brand.site_name}
                        </span>
                        {brand.tagline && (
                            <p className="mt-4 text-sm text-dim leading-relaxed max-w-xs">{brand.tagline}</p>
                        )}
                        <div className="rule my-5 max-w-[3rem]" />
                        <p className="font-mono text-xs text-dim" data-testid="footer-contact-email">{brand.email}</p>
                        {brand.phone && <p className="mt-1.5 font-mono text-xs text-dim">{brand.phone}</p>}
                    </div>

                    {/* Sections */}
                    <div className="lg:px-8">
                        <h5 className="eyebrow text-accent pb-3 mb-4 border-b border-line">Sections</h5>
                        <ul className="space-y-2.5">
                            {CATEGORIES.map((c) => (
                                <li key={c}>
                                    <Link to={`/category/${c}`} className="text-sm text-foreground hover:text-accent link-underline transition-colors duration-150">
                                        {CATEGORY_LABELS[c]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div className="lg:px-8">
                        <h5 className="eyebrow text-accent pb-3 mb-4 border-b border-line">Company</h5>
                        <ul className="space-y-2.5">
                            <li><Link to="/advertise" data-testid="footer-advertise" className="text-sm text-foreground hover:text-accent link-underline transition-colors duration-150">Advertise with us</Link></li>
                            <li><Link to="/archive" className="text-sm text-foreground hover:text-accent link-underline transition-colors duration-150">Archive</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className="text-sm text-foreground hover:text-accent link-underline transition-colors duration-150">Privacy Policy</Link></li>
                            <li><Link to="/terms" data-testid="footer-terms" className="text-sm text-foreground hover:text-accent link-underline transition-colors duration-150">Terms of Use</Link></li>
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div className="lg:pl-8">
                        <h5 className="eyebrow text-accent pb-3 mb-4 border-b border-line" data-testid="footer-newsletter-title">Newsletter</h5>
                        <div className="bg-accent-soft border border-line p-5">
                            <p className="text-sm text-foreground leading-relaxed">
                                Get the latest financial news delivered to your inbox.
                            </p>
                            <Link
                                to="/register"
                                data-testid="footer-newsletter-subscribe"
                                className="mt-4 inline-block eyebrow bg-accent text-accent-foreground px-6 py-3 hover:bg-foreground transition-colors duration-150"
                            >
                                Subscribe
                            </Link>
                        </div>
                        <button
                            onClick={() => setUnsubOpen(true)}
                            data-testid="footer-unsubscribe"
                            className="mt-4 block text-xs text-dim hover:text-neg transition-colors duration-150"
                        >
                            Unsubscribe
                        </button>
                    </div>
                </div>
            </div>

            {/* Closing emerald strip */}
            <div className="bg-accent text-accent-foreground">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-accent-foreground/80">
                        © {new Date().getFullYear()} {brand.site_name} · {brand.domain}
                    </p>
                </div>
            </div>
            <UnsubscribeModal open={unsubOpen} onOpenChange={setUnsubOpen} />
        </footer>
    );
}
